// src/deeplinkHandler.js
import { GoogleAuthProvider, signInWithCredential } from "firebase/auth";
import { firebaseAuth } from "./firebase";

function parseUrlParams(url) {
  try {
    const u = new URL(url);
    const hash = u.hash && u.hash.startsWith("#") ? u.hash.slice(1) : u.hash;
    const search = u.search && u.search.startsWith("?") ? u.search.slice(1) : u.search;

    const params = new URLSearchParams(search || "");
    if (hash) {
      const hParams = new URLSearchParams(hash);
      hParams.forEach((v, k) => params.set(k, v));
    }

    const out = {};
    for (const [k, v] of params.entries()) out[k] = v;
    return out;
  } catch {
    return {};
  }
}

async function tryCloseBrowser() {
  try {
    const mod = await import("@capacitor/browser");
    if (mod?.Browser?.close) await mod.Browser.close();
  } catch {
    // ignore
  }
}

async function handleUrl(url, onSuccess) {
  if (!url) return false;

  const params = parseUrlParams(url);
  if (!params.id_token) return false;

  const idToken = params.id_token;
  const cred = GoogleAuthProvider.credential(idToken);

  // Close the external browser/tab if possible
  await tryCloseBrowser();

  try {
    const userCred = await signInWithCredential(firebaseAuth, cred);
    console.log("[deeplink] signInWithCredential success:", userCred?.user?.email);
    if (typeof onSuccess === "function") onSuccess(userCred);
    return true;
  } catch (fbErr) {
    console.error("[deeplink] signInWithCredential failed:", fbErr);
    return false;
  }
}

/**
 * registerDeepLinkHandler(onSuccess)
 */
export async function registerDeepLinkHandler(onSuccess) {
  // 1) Native handler: appUrlOpen
  try {
    const mod = await import("@capacitor/app");
    const { App } = mod;

    App.addListener("appUrlOpen", async (event) => {
      try {
        const url = event?.url || null;
        console.log("[deeplink] appUrlOpen:", url);
        await handleUrl(url, onSuccess);
      } catch (e) {
        console.error("[deeplink] appUrlOpen handler error:", e);
      }
    });

    console.info("[deeplink] registered appUrlOpen listener via @capacitor/app");

    // 2) Cold-start / resumed: check current URL as well (sometimes tokens arrive here)
    try {
      const current = typeof window !== "undefined" ? window.location.href : null;
      await handleUrl(current, onSuccess);
    } catch {}

    return;
  } catch (e) {
    console.warn("[deeplink] @capacitor/app not available. Web fallback only.", e);
  }

  // Web fallback
  try {
    const handleLocation = async () => {
      const cur = typeof window !== "undefined" ? window.location.href : null;
      await handleUrl(cur, onSuccess);
    };

    await handleLocation();

    if (typeof window !== "undefined") {
      window.addEventListener("hashchange", handleLocation, false);
    }
    console.info("[deeplink] web fallback registered (hashchange)");
  } catch (e2) {
    console.error("[deeplink] web fallback registration failed:", e2);
  }
}